export function createDbfFile(features: any[]): Blob {
  if (!features.length) {
    return new Blob([], { type: 'application/octet-stream' });
  }

  const keySet = new Set<string>();
  features.forEach((feature: any) => {
    const attrs = feature.attributes || feature.properties || {};
    Object.keys(attrs).forEach((k) => keySet.add(k));
  });
  const sortedKeys = [...keySet].sort();

  const fields: {
    name: string;
    type: string;
    length: number;
    decimals: number;
    sourceKey: string;
  }[] = [];

  const sampleForKey = (key: string): unknown => {
    for (const feature of features) {
      const attrs = feature.attributes || feature.properties || {};
      if (
        Object.prototype.hasOwnProperty.call(attrs, key) &&
        attrs[key] != null
      ) {
        return attrs[key];
      }
    }
    const first = features[0].attributes || features[0].properties || {};
    return first[key];
  };

  sortedKeys.forEach((key) => {
    const truncatedName = key.substring(0, 10);
    const sampleValue = sampleForKey(key);
    let fieldType: string,
      fieldLength: number,
      fieldDecimals = 0;
    if (typeof sampleValue === 'number') {
      fieldType = 'N';
      fieldLength = 18;
      fieldDecimals = Number.isInteger(sampleValue) ? 0 : 2;
    } else if (sampleValue instanceof Date) {
      fieldType = 'D';
      fieldLength = 8;
    } else {
      fieldType = 'C';
      let maxLen = 0;
      features.forEach((feature: any) => {
        const attrs = feature.attributes || feature.properties || {};
        const val = attrs[key];
        if (val != null) {
          const str = String(val);
          if (str.length > maxLen) maxLen = str.length;
        }
      });
      fieldLength = Math.min(Math.max(maxLen, 1), 254);
    }
    fields.push({
      name: truncatedName,
      type: fieldType,
      length: fieldLength,
      decimals: fieldDecimals,
      sourceKey: key,
    });
  });

  const numRecords = features.length;
  const numFields = fields.length;
  const headerLength = 32 + 32 * numFields + 1;

  let recordLength = 1;
  fields.forEach((field) => {
    recordLength += field.length;
  });

  const bufferSize = headerLength + recordLength * numRecords + 1;
  const buffer = new ArrayBuffer(bufferSize);
  const view = new DataView(buffer);

  view.setUint8(0, 0x03);
  const now = new Date();
  view.setUint8(1, now.getFullYear() - 1900);
  view.setUint8(2, now.getMonth() + 1);
  view.setUint8(3, now.getDate());
  view.setInt32(4, numRecords, true);
  view.setInt16(8, headerLength, true);
  view.setInt16(10, recordLength, true);
  for (let i = 12; i < 32; i++) {
    view.setUint8(i, 0);
  }

  let offset = 32;
  fields.forEach((field) => {
    const nameBytes = new TextEncoder().encode(field.name);
    for (let i = 0; i < 11; i++) {
      view.setUint8(offset + i, i < nameBytes.length ? nameBytes[i] : 0);
    }
    view.setUint8(offset + 11, field.type.charCodeAt(0));
    for (let i = 12; i < 16; i++) {
      view.setUint8(offset + i, 0);
    }
    view.setUint8(offset + 16, field.length);
    view.setUint8(offset + 17, field.decimals);
    for (let i = 18; i < 32; i++) {
      view.setUint8(offset + i, 0);
    }
    offset += 32;
  });
  view.setUint8(offset, 0x0d);
  offset++;

  features.forEach((feature: any) => {
    view.setUint8(offset, 0x20);
    offset++;
    fields.forEach((field) => {
      const attrs = feature.attributes || feature.properties || {};
      let val: unknown = attrs[field.sourceKey];
      if (val === undefined) {
        const keyMatch = Object.keys(attrs).find(
          (key) => key.substring(0, 10) === field.name,
        );
        val = keyMatch ? attrs[keyMatch] : '';
      }
      let text = '';
      if (field.type === 'N') {
        text = typeof val === 'number' ? val.toFixed(field.decimals) : '0';
        text = text.padStart(field.length, ' ');
      } else if (field.type === 'D') {
        if (val instanceof Date) {
          const y = val.getFullYear().toString().padStart(4, '0');
          const m = (val.getMonth() + 1).toString().padStart(2, '0');
          const d = val.getDate().toString().padStart(2, '0');
          text = y + m + d;
        } else {
          text = ' ';
        }
      } else {
        text = String(val ?? '').padEnd(field.length, ' ');
      }
      if (text.length > field.length) {
        text = text.substring(0, field.length);
      }
      const encoded = new TextEncoder().encode(text);
      for (let i = 0; i < field.length; i++) {
        view.setUint8(offset + i, i < encoded.length ? encoded[i] : 32);
      }
      offset += field.length;
    });
  });
  view.setUint8(offset, 0x1a);

  return new Blob([buffer], { type: 'application/octet-stream' });
}
